import { useMemo } from 'react';
import { useHistory } from 'react-router';
import ReactSelect, {
  GroupBase,
  GroupHeadingProps,
  SingleValue,
} from 'react-select';
import { useDisclosure } from '@chakra-ui/react';

import { DEFAULT_ILLUSTRATION_NAME } from 'common/bankAccount/constants';
import {
  getBankAccountDisplayName,
  getTotalBalance,
} from 'common/bankAccount/utils';
import { useCanCreateAdditionalWallet } from 'common/hooks/useCanCreateAdditionalWallet';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Loader from 'components/Loader';
import MultiWalletUpsellModal from 'components/MultiWalletUpsellModal';

import * as routes from '../../features/Bank/routes';
import {
  ControlComponent,
  IndicatorsContainerComponent,
  NewAccountComponent,
  OptionComponent,
  SingleValueComponent,
} from './components';
import { ALL_ACCOUNTS_VALUE, PLACEHOLDER_ACCOUNT_VALUE } from './constants';
import locales from './locales';
import { styles } from './styles';
import { OptionData } from './types';

interface BankAccountPickerProps {
  onBankAccountChange?: (bankAccountId: string | undefined) => void;
  selectedBankAccountId?: string;
  withCreateAccountOption?: boolean;
  withAllAccountsOption?: boolean;
}

const placeholderOption: OptionData = {
  currency: 'EUR',
  currentBalance: 0,
  disabled: true,
  illustrationName: DEFAULT_ILLUSTRATION_NAME,
  name: locales.mainAccount,
  value: PLACEHOLDER_ACCOUNT_VALUE,
};

const placeholderOptionsGroup: GroupBase<OptionData> = {
  label: locales.yourAccounts,
  options: [placeholderOption],
};

const BankAccountPicker = ({
  onBankAccountChange,
  selectedBankAccountId,
  withAllAccountsOption = false,
  withCreateAccountOption = false,
}: BankAccountPickerProps) => {
  const { bankAccounts } = useCompanyBankAccounts();
  const canCreateAdditionalWallet = useCanCreateAdditionalWallet();
  const history = useHistory();
  const { plan } = useCurrentPricingPlan();
  const {
    isOpen: isUpgradeModalOpen,
    onClose: onUpgradeModalClose,
    onOpen: onUpgradeModalOpen,
  } = useDisclosure();

  const totalBalance = getTotalBalance({
    bankAccounts,
    key: 'currentBalance',
  });

  const allAccountsOption: OptionData = useMemo(
    () => ({
      currency: 'EUR',
      currentBalance: totalBalance,
      illustrationName: DEFAULT_ILLUSTRATION_NAME,
      name: locales.allAccounts,
      value: ALL_ACCOUNTS_VALUE,
    }),
    [totalBalance],
  );

  const bankAccountOptions = bankAccounts.map(
    ({ bankAccountId, currentBalance, name, type, ...rest }) => {
      const finalName = getBankAccountDisplayName({ name, type });
      return {
        ...rest,
        currentBalance: currentBalance ?? 0,
        label: finalName,
        name: finalName,
        value: bankAccountId,
      } as OptionData;
    },
  );

  const bankAccountOptionsGroup = useMemo(
    () => ({
      label: locales.yourAccounts,
      options: bankAccountOptions,
    }),
    [bankAccountOptions],
  );

  const selectBankAccountOptions = useMemo(() => {
    const options: GroupBase<OptionData>[] = [bankAccountOptionsGroup];
    if (withAllAccountsOption) {
      bankAccountOptionsGroup.options.unshift(allAccountsOption);
    } else if (bankAccountOptions.length === 0) {
      // If bank accounts are still loading and we don't want to show the all accounts option,
      // we show a placeholder option instead.
      options.unshift(placeholderOptionsGroup);
    }

    return options;
  }, [
    bankAccountOptions,
    allAccountsOption,
    withAllAccountsOption,
    bankAccountOptionsGroup,
  ]);

  const hasMultipleBankAccounts = bankAccounts.length > 1;
  if (
    !hasMultipleBankAccounts &&
    !withCreateAccountOption &&
    !withAllAccountsOption
  ) {
    return null;
  }

  if (!plan) {
    return <Loader />;
  }

  const {
    limits: { activeBankAccounts },
    upgradablePlanIds,
  } = plan;
  const canAccessUpgrade = upgradablePlanIds?.length > 0;
  const walletLimit = activeBankAccounts.value - 1;

  const GroupHeadingComponent = (props: GroupHeadingProps<OptionData>) => {
    const componentProps = {
      ...props,
      disabled: !canCreateAdditionalWallet.value && !canAccessUpgrade,
      display: withCreateAccountOption,
      onClick: () => {
        if (canCreateAdditionalWallet.value) {
          history.push(routes.newBankAccount);
        } else if (canAccessUpgrade) {
          onUpgradeModalOpen();
        }
      },
      tooltip: !canAccessUpgrade ? canCreateAdditionalWallet.description : null,
    };
    return <NewAccountComponent {...componentProps} />;
  };

  const selectedOption = bankAccountOptionsGroup.options.find(
    ({ value }) => value === selectedBankAccountId,
  );

  const handleChange = (option: SingleValue<OptionData>) => {
    switch (option?.value) {
      case ALL_ACCOUNTS_VALUE:
        onBankAccountChange?.(undefined);
        return;
      default:
        onBankAccountChange?.(option?.value);
    }
  };

  return (
    <>
      <ReactSelect<OptionData>
        components={{
          Control: ControlComponent,
          GroupHeading: GroupHeadingComponent,
          IndicatorsContainer: IndicatorsContainerComponent,
          Option: OptionComponent,
          SingleValue: SingleValueComponent,
        }}
        defaultValue={
          withAllAccountsOption ? allAccountsOption : placeholderOption
        }
        isOptionDisabled={({ disabled }) => Boolean(disabled)}
        isSearchable={false}
        onChange={handleChange}
        options={selectBankAccountOptions}
        styles={styles}
        value={selectedOption}
      />
      {isUpgradeModalOpen && canAccessUpgrade ? (
        <MultiWalletUpsellModal
          isOpen={isUpgradeModalOpen}
          onClose={onUpgradeModalClose}
          walletLimit={walletLimit}
        />
      ) : null}
    </>
  );
};

export default BankAccountPicker;
